import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Button, Input } from 'antd';
import bgFocus from 'assets/images/wc2026/bg_focus.png';
import bgUnfocus from 'assets/images/wc2026/bg_unfocus.png';
import wcBg from 'assets/images/wc2026/wc_bg.png';
import wcTitleBg from 'assets/images/wc2026/wc_title_bg.png';
import top10Bg2 from 'assets/images/wc2026/top_10_bg_2.png';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { twMerge } from 'tailwind-merge';
import { getStoredAccessToken, requestOpenLoginModal } from 'utils/authSession';

/** Top 10 — Figma 72-951, nền top_10_bg_2.png */
const RankBadgeTop10 = ({ rank }) => {
  const base =
    'flex h-[35px] w-[35px] shrink-0 items-center justify-center rounded-sm font-black text-[15px] md:text-[16px] leading-none tabular-nums';
  if (rank === 1) {
    return (
      <div
        className={twMerge(
          base,
          'bg-gradient-to-b from-[#fff241] to-[#f9c907] text-[#fff8db] shadow-[0_4px_4px_rgba(0,0,0,0.25)] [text-shadow:0_1px_2px_rgba(0,0,0,0.4)] ring-1 ring-[#fffa]/50'
        )}
      >
        1
      </div>
    );
  }
  if (rank === 2) {
    return (
      <div
        className={twMerge(
          base,
          'bg-gradient-to-b from-[#eaeef2] to-[#575757] text-white shadow-[0_4px_4px_rgba(0,0,0,0.25)] [text-shadow:0_1px_2px_rgba(0,0,0,0.4)] ring-1 ring-white/30'
        )}
      >
        2
      </div>
    );
  }
  if (rank === 3) {
    return (
      <div
        className={twMerge(
          base,
          'bg-gradient-to-b from-[#ee864c] to-[#be4611] text-white shadow-[0_4px_4px_rgba(0,0,0,0.25)] [text-shadow:0_1px_2px_rgba(0,0,0,0.4)] ring-1 ring-[#f8f5da]/40'
        )}
      >
        3
      </div>
    );
  }
  return (
    <div
      className={twMerge(base, 'bg-gradient-to-b from-[#50e2ff] to-[#184c62] text-white shadow-sm')}
    >
      {rank}
    </div>
  );
};

const ChampionTop10Panel = ({ rows, resolvePhoto, onRowClick }) => {
  const list = (rows || []).slice(0, 10);
  const rowClass =
    'flex h-[35px] w-full min-w-0 shrink-0 overflow-hidden rounded-md shadow-[0_1px_3px_rgba(0,0,0,0.12)] transition-[transform,filter]';
  return (
    <div className="min-w-0 lg:col-span-2">
      <div
        className="relative mx-auto h-[480px] w-full max-w-[400px] overflow-hidden rounded-2xl md:h-[600px] lg:mx-0 lg:max-w-none"
        style={{
          backgroundImage: `url(${top10Bg2})`,
          backgroundSize: 'contain',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="absolute inset-x-0 top-[120px] h-[320px] md:h-[400px] bottom-3 z-[1] flex justify-center px-2 md:top-[150px] md:bottom-4">
          <div className="flex h-[90%] md:h-full w-[90%] md:w-full max-w-[350px] flex-col">
            {list.length === 0 ? (
              <p className="py-6 text-center text-sm font-medium text-[#191919]/80">
                Chưa có dữ liệu bình chọn.
              </p>
            ) : (
              <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-x-hidden overflow-y-auto overscroll-y-contain [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                {list.map((row, idx) => {
                  const rank = idx + 1;
                  const photo = resolvePhoto(row);
                  const inner = (
                    <>
                      <RankBadgeTop10 rank={rank} />
                      <div className="relative h-[35px] w-[35px] shrink-0 overflow-hidden bg-[#faa]/25">
                        {photo ? (
                          <img
                            src={photo}
                            alt=""
                            className="h-full w-full object-cover object-center"
                            draggable={false}
                          />
                        ) : (
                          <div className="h-full w-full bg-white/40" />
                        )}
                      </div>
                      <div className="flex h-[35px] min-w-0 flex-1 items-center justify-between gap-2 bg-gradient-to-l from-[#e9fdff] to-white pl-3 pr-4">
                        <span className="min-w-0 truncate font-bold uppercase text-[#191919] text-[13px] leading-tight sm:text-[14px] md:text-[16px]">
                          {row.name}
                        </span>
                        <span className="shrink-0 font-normal tabular-nums text-[#191919] text-[13px] sm:text-[14px] md:text-[16px]">
                          {Intl.NumberFormat('vi-VN').format(row.votes || 0)}
                        </span>
                      </div>
                    </>
                  );
                  if (onRowClick) {
                    return (
                      <button
                        key={`${row.id}-${rank}`}
                        type="button"
                        onClick={() => onRowClick(row)}
                        className={twMerge(
                          rowClass,
                          'cursor-pointer border-0 bg-transparent p-0 text-left hover:brightness-[1.03] active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#26A9E0]/45 focus-visible:ring-offset-1'
                        )}
                      >
                        {inner}
                      </button>
                    );
                  }
                  return (
                    <div key={`${row.id}-${rank}`} className={rowClass}>
                      {inner}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Carousel 3 mục (trái / giữa focus / phải), vòng lặp — đội hoặc cầu thủ.
 */
const ChampionCarouselVotePanel = ({
  title,
  query,
  onQueryChange,
  onSubmit,
  loading,
  items,
  /** Danh sách đầy đủ để gợi ý (mặc định = items) */
  suggestionItems,
  selectedId,
  onSelect,
  emptyText,
  getItemPhoto,
  searchPlaceholder,
  getItemSubline,
  submitButtonText = 'Bình chọn'
}) => {
  const n = items.length;
  const suggestWrapRef = useRef(null);
  const suggestDropdownRef = useRef(null);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestDropdownRect, setSuggestDropdownRect] = useState(null);

  const suggestSource = suggestionItems ?? items;

  const filteredSuggestions = useMemo(() => {
    const list = suggestSource || [];
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((item) => {
      const name = String(item.name || '').toLowerCase();
      const sub = String(getItemSubline?.(item) || item.teamName || '').toLowerCase();
      return name.includes(q) || sub.includes(q);
    });
  }, [suggestSource, query, getItemSubline]);

  const updateSuggestDropdownPosition = useCallback(() => {
    const el = suggestWrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vh = window.visualViewport?.height ?? window.innerHeight;
    const gap = 8;
    /** Cạnh dưới list (fixed) nằm ngay phía trên mép trên ô input */
    const bottom = Math.round(vh - r.top + gap);
    const left = Math.round(r.left);
    const width = Math.round(r.width);
    setSuggestDropdownRect((prev) => {
      if (prev && prev.bottom === bottom && prev.left === left && prev.width === width) return prev;
      return { bottom, left, width };
    });
  }, []);

  useLayoutEffect(() => {
    if (!suggestOpen || filteredSuggestions.length === 0) {
      setSuggestDropdownRect(null);
      return;
    }
    updateSuggestDropdownPosition();
  }, [suggestOpen, filteredSuggestions.length, query, updateSuggestDropdownPosition]);

  useEffect(() => {
    if (!suggestOpen || filteredSuggestions.length === 0) return;
    updateSuggestDropdownPosition();
    const onScrollOrResize = () => updateSuggestDropdownPosition();
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('scroll', onScrollOrResize, true);
    return () => {
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [suggestOpen, filteredSuggestions.length, updateSuggestDropdownPosition]);

  /** Cuộn layout gốc (window) để popup fixed không bị cắt dưới / trên viewport */
  const scrollSuggestIntoViewport = useCallback(() => {
    const listEl = suggestDropdownRef.current;
    if (!listEl) return;
    const rect = listEl.getBoundingClientRect();
    const margin = 16;
    const vh = window.visualViewport?.height ?? window.innerHeight;
    if (rect.bottom <= vh - margin && rect.top >= margin) return;
    if (rect.bottom > vh - margin) {
      window.scrollBy({ top: rect.bottom - (vh - margin), behavior: 'smooth' });
    } else if (rect.top < margin) {
      window.scrollBy({ top: rect.top - margin, behavior: 'smooth' });
    }
  }, []);

  /** Sau khi portal gắn DOM; chỉ cuộn khi popup tràn viewport (tránh loop với cập nhật vị trí theo scroll) */
  useLayoutEffect(() => {
    if (!suggestOpen || !suggestDropdownRect || filteredSuggestions.length === 0) return;
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => scrollSuggestIntoViewport());
    });
    return () => cancelAnimationFrame(id);
  }, [suggestOpen, suggestDropdownRect, filteredSuggestions.length, query, scrollSuggestIntoViewport]);

  useEffect(() => {
    if (!suggestOpen) return;
    const onDocMouseDown = (e) => {
      const t = e.target;
      if (suggestWrapRef.current?.contains(t)) return;
      if (suggestDropdownRef.current?.contains(t)) return;
      setSuggestOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [suggestOpen]);

  const currentIndex = useMemo(() => {
    if (!n) return 0;
    const idx = items.findIndex((t) => String(t.id) === String(selectedId));
    return idx >= 0 ? idx : 0;
  }, [items, selectedId, n]);

  useEffect(() => {
    if (!n) return;
    const idx = items.findIndex((t) => String(t.id) === String(selectedId));
    if (idx < 0 && items[0]) {
      onSelect(items[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onSelect identity không cần theo dõi
  }, [items, selectedId, n]);

  const leftItem = n ? items[(currentIndex - 1 + n) % n] : null;
  const centerItem = n ? items[currentIndex] : null;
  const rightItem = n ? items[(currentIndex + 1) % n] : null;

  const move = (direction) => {
    if (!n) return;
    const nextIndex = (currentIndex + direction + n) % n;
    onSelect(items[nextIndex]);
  };

  const handleVoteButtonClick = () => {
    if (!getStoredAccessToken()) {
      requestOpenLoginModal();
      return;
    }
    onSubmit();
  };

  const renderCard = (item, isCenter, slot, onActivate) => {
    if (!item) return null;
    const photo = getItemPhoto(item);
    const subline = getItemSubline?.(item);

    return (
      <button
        key={`${slot}-${item.id}`}
        type="button"
        onClick={onActivate}
        className={twMerge(
          'relative flex-none overflow-hidden border-none bg-transparent p-0 text-left transition',
          isCenter
            ? 'h-[188px] w-[136px] opacity-100 sm:h-[194px] sm:w-[140px] md:h-[332px] md:w-[242px]'
            : 'h-[128px] w-[92px] opacity-80 blur-[1.5px] sm:h-[140px] sm:w-[100px] md:h-[232px] md:w-[168px]'
        )}
      >
        <img
          src={isCenter ? bgFocus : bgUnfocus}
          alt=""
          className="pointer-events-none absolute inset-0 h-full w-full object-contain object-center"
          draggable={false}
        />
        <div
          className={twMerge(
            'absolute left-1/2 z-10 -translate-x-1/2',
            isCenter ? 'top-[44.33%] -translate-y-1/2' : 'top-[43.48%] -translate-y-1/2'
          )}
        >
          <div
            className={twMerge(
              'rounded-full border-[0.5px] border-white bg-white shadow-[0_2px_8px_rgba(0,0,0,0.18)]',
              isCenter
                ? 'h-[60px] w-[60px] p-[2px] sm:h-[62px] sm:w-[62px] md:h-[104px] md:w-[106px]'
                : 'h-[42px] w-[42px] p-[2px] sm:h-[44px] sm:w-[44px] md:h-[72px] md:w-[73px]'
            )}
          >
            <div className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-full bg-white">
              {photo ? (
                <img
                  src={photo}
                  alt=""
                  className="h-full w-full object-cover object-center"
                  style={{ transform: 'scale(1.08)' }}
                  draggable={false}
                />
              ) : (
                <span className="text-[10px] text-gray-400">?</span>
              )}
            </div>
          </div>
        </div>
        <div
          className={twMerge(
            'absolute left-1/2 z-10 w-[calc(100%-8px)] max-w-none -translate-x-1/2 px-0.5 text-center text-white',
            isCenter
              ? subline
                ? 'top-[68%]'
                : 'top-[73.31%]'
              : subline
                ? 'top-[64%]'
                : 'top-[70.26%]'
          )}
        >
          <div
            className={twMerge(
              'font-black uppercase leading-tight drop-shadow-[0_1px_2px_rgba(0,0,0,0.45)] line-clamp-2',
              isCenter
                ? 'text-[10px] leading-[1.17] sm:text-[12px] md:text-[16px]'
                : 'text-[7px] leading-[1.17] sm:text-[9px] md:text-[12px]'
            )}
          >
            {item.name}
          </div>
          {subline ? (
            <div
              className={twMerge(
                'mt-0.5 font-semibold normal-case leading-tight line-clamp-1 opacity-95 drop-shadow-[0_1px_2px_rgba(0,0,0,0.45)]',
                isCenter
                  ? 'text-[8px] sm:text-[9px] md:text-[11px]'
                  : 'text-[6px] sm:text-[7px] md:text-[9px]'
              )}
            >
              {subline}
            </div>
          ) : null}
        </div>
      </button>
    );
  };

  return (
    <div
      className={twMerge(
        'relative mx-auto flex w-full max-w-[750px] flex-col items-center overflow-visible rounded-[40px]',
        'border-2 border-[#99DAE8] mt-4 md:mt-8 px-3 pb-3 md:px-2 md:pb-5',
        'shadow-[inset_0_0_0_1px_rgba(255,255,255,0.25)]'
      )}
    >
      <div
        className="pointer-events-none absolute inset-0 rounded-[40px] bg-cover bg-center"
        style={{ backgroundImage: `url(${wcBg})` }}
      />

      <div className="relative z-10 flex w-full max-w-[90%] flex-col items-center gap-6 overflow-visible md:gap-7">
        <div className="relative -mt-3 md:-mt-3 flex w-full justify-center px-2">
          <div className="relative h-[44px] w-full max-w-[366px] md:inline-flex md:h-[50px] md:w-auto md:max-w-none md:min-w-[366px] md:items-center md:justify-center md:px-10">
            <img
              src={wcTitleBg}
              alt=""
              className="h-full w-full object-contain md:absolute md:inset-0 md:object-fill"
              draggable={false}
            />
            <div className="absolute inset-0 z-10 grid place-items-center px-6 text-center text-[14px] font-black uppercase text-white drop-shadow-[0_3px_4px_rgba(0,20,42,0.5)] md:relative md:inset-auto md:px-0 md:text-[20px] md:leading-tight md:whitespace-nowrap">
              {title}
            </div>
          </div>
        </div>

        {!n ? (
          <div className="flex min-h-[200px] w-full items-center justify-center px-4 text-center text-sm text-white/90">
            {emptyText}
          </div>
        ) : (
          <div className="-mt-1 flex w-full items-center justify-center gap-1 md:gap-2">
            <button
              type="button"
              aria-label="Trước"
              onClick={() => move(-1)}
              className="hidden grid h-8 w-8 shrink-0 place-items-center rounded-full border-0 bg-white/15 text-[#26A9E0] backdrop-blur-sm transition hover:bg-white/25 md:h-10 md:w-10"
            >
              <LeftOutlined className="text-[14px] md:text-[16px]" />
            </button>

            <div className="flex min-h-[142px] flex-1 items-center justify-center gap-1.5 overflow-hidden sm:min-h-[150px] sm:gap-2 md:min-h-[158px] md:gap-3">
              {renderCard(leftItem, false, 'left', () => move(-1))}
              {renderCard(centerItem, true, 'center', () => centerItem && onSelect(centerItem))}
              {renderCard(rightItem, false, 'right', () => move(1))}
            </div>

            <button
              type="button"
              aria-label="Sau"
              onClick={() => move(1)}
              className="hidden grid h-8 w-8 shrink-0 place-items-center rounded-full border-0 bg-white/15 text-[#26A9E0] backdrop-blur-sm transition hover:bg-white/25 md:h-10 md:w-10"
            >
              <RightOutlined className="text-[14px] md:text-[16px]" />
            </button>
          </div>
        )}

        <div className="relative z-20 mt-3 flex w-full max-w-[360px] flex-col justify-center items-center gap-3 overflow-visible px-1 sm:items-center sm:gap-4">
          <div
            ref={suggestWrapRef}
            className="relative z-[1] min-w-0 w-full flex-1 overflow-visible"
          >
            <div className="rounded-full p-[3px] [background:linear-gradient(180deg,#d6e0e2_0%,#ffffff_100%)]">
              <Input
                value={query}
                onChange={(e) => onQueryChange(e.target.value)}
                onFocus={() => setSuggestOpen(true)}
                placeholder={searchPlaceholder}
                className="h-11 rounded-full border-0 text-center text-[14px] font-normal uppercase text-[#3C3C3C] placeholder:normal-case placeholder:text-[#6b7280] md:h-12 md:text-base"
                style={{
                  background: 'linear-gradient(0deg, #d7dfe6 0%, #ffffff 100%)'
                }}
                autoComplete="off"
              />
            </div>
          </div>
          {suggestOpen &&
            filteredSuggestions.length > 0 &&
            suggestDropdownRect &&
            typeof document !== 'undefined' &&
            createPortal(
              <ul
                ref={suggestDropdownRef}
                style={{
                  position: 'fixed',
                  top: 'auto',
                  bottom: suggestDropdownRect.bottom,
                  left: suggestDropdownRect.left,
                  width: suggestDropdownRect.width,
                  maxHeight: 'min(360px, 55vh)',
                  overflowY: 'auto',
                  zIndex: 2500
                }}
                className="m-0 list-none rounded-t-2xl rounded-b-[20px] bg-white py-1.5 pl-0 shadow-[0_10px_40px_rgba(15,23,42,0.1)] [scrollbar-width:thin]"
                role="listbox"
                aria-label="Gợi ý"
              >
                {filteredSuggestions.map((item) => {
                  const sub = getItemSubline?.(item) || item.teamName;
                  const isSelected = String(selectedId) === String(item.id);
                  return (
                    <li key={item.id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        className="flex w-full flex-col items-start gap-0.5 border-0 bg-white px-3 py-2 text-left shadow-none outline-none ring-0 transition-colors hover:bg-slate-50 focus-visible:bg-slate-50"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          onSelect(item);
                          setSuggestOpen(false);
                        }}
                      >
                        <span className="text-[14px] font-bold leading-tight text-[#0f172a] normal-case">
                          {item.name}
                        </span>
                        {sub ? (
                          <span className="text-[12px] font-medium leading-tight text-[#64748b] normal-case">
                            {sub}
                          </span>
                        ) : null}
                      </button>
                    </li>
                  );
                })}
              </ul>,
              document.body
            )}
          <Button
            type="primary"
            loading={loading}
            disabled={!n}
            onClick={handleVoteButtonClick}
            className="relative h-9 max-w-[240px] shrink-0 whitespace-nowrap rounded-full border border-white px-6 text-[14px] font-bold uppercase text-white shadow-none md:h-10 md:px-8 md:text-[16px]"
            style={{
              background: 'linear-gradient(180deg, #35c4fb 0%, #0d91c8 100%)',
              minWidth: 130
            }}
          >
            {submitButtonText}
          </Button>
        </div>
      </div>
    </div>
  );
};

export const ChampionPredictionTab = ({
  panelTitle = 'Dự đoán đội vô địch',
  searchPlaceholder = 'Tìm tên đội...',
  emptyText = 'Không tìm thấy đội phù hợp.',
  championLeaderboard,
  championTeams,
  filteredChampionTeams,
  championQuery,
  setChampionQuery,
  isChampionVoting,
  onSubmitChampion,
  selectedChampionId,
  setSelectedChampionId
}) => {
  return (
    <div className="mt-3 grid w-full grid-cols-1 items-start gap-4 lg:grid-cols-5">
      <div className="min-w-0 lg:col-span-3">
        <ChampionCarouselVotePanel
          title={panelTitle}
          query={championQuery}
          onQueryChange={setChampionQuery}
          onSubmit={onSubmitChampion}
          loading={isChampionVoting}
          items={filteredChampionTeams}
          suggestionItems={championTeams}
          selectedId={selectedChampionId}
          onSelect={(team) => setSelectedChampionId(team.id)}
          emptyText={emptyText}
          getItemPhoto={(team) => team.flag}
          searchPlaceholder={searchPlaceholder}
        />
      </div>
      <ChampionTop10Panel
        rows={championLeaderboard}
        resolvePhoto={(row) => {
          const team = championTeams.find((t) => String(t.id) === String(row.id));
          return row.flag ?? team?.flag;
        }}
        onRowClick={(row) => {
          const norm = (s) =>
            String(s || '')
              .trim()
              .toLowerCase();
          const team =
            championTeams.find((t) => String(t.id) === String(row.id)) ||
            championTeams.find((t) => norm(t.name) === norm(row.name));
          const id = team?.id ?? row.id;
          setSelectedChampionId(id, team ? undefined : row.name);
        }}
      />
    </div>
  );
};

export const PlayerPredictionTab = ({
  title,
  leaderboard,
  players,
  /** Toàn bộ cầu thủ để gợi ý (carousel có thể đang filter) */
  suggestionPlayers,
  query,
  setQuery,
  selectedId,
  setSelectedId,
  loading,
  onSubmit
}) => {
  const fullPlayerList = suggestionPlayers ?? players;
  return (
    <div className="mt-3 grid w-full grid-cols-1 items-start gap-4 lg:grid-cols-5">
      <div className="min-w-0 lg:col-span-3">
        <ChampionCarouselVotePanel
          title={title}
          query={query}
          onQueryChange={setQuery}
          onSubmit={onSubmit}
          loading={loading}
          items={players}
          suggestionItems={fullPlayerList}
          selectedId={selectedId}
          onSelect={(player) => setSelectedId(player.id)}
          emptyText="Không tìm thấy cầu thủ phù hợp."
          getItemPhoto={(player) => player.photo}
          searchPlaceholder="Tìm cầu thủ yêu thích của bạn..."
          getItemSubline={(player) => player.teamName || null}
          submitButtonText="Dự đoán"
        />
      </div>
      <ChampionTop10Panel
        rows={leaderboard}
        resolvePhoto={(row) => {
          const pl = players.find((p) => String(p.id) === String(row.id));
          return row.photo ?? row.flag ?? pl?.photo;
        }}
        onRowClick={(row) => setSelectedId(row.id)}
      />
    </div>
  );
};
